/**
 * Thin fetch wrapper with automatic access-token refresh.
 *
 * A 401 triggers exactly one refresh attempt, and concurrent requests that all
 * hit 401 share that single attempt rather than each starting their own - an
 * unguarded refresh storm is the usual way a token-rotating API logs everyone
 * out at once.
 */

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = 'rfmon.tokens';

/**
 * Tokens are held in sessionStorage, not localStorage: closing the tab ends
 * the session, which is what a shared operations console wants. Every access
 * is wrapped because storage throws in private-browsing contexts.
 */
export function loadTokens(): Tokens | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function saveTokens(tokens: Tokens | null): void {
  try {
    if (tokens) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the session simply does not survive a reload.
  }
}

let refreshInFlight: Promise<boolean> | null = null;
let onUnauthorised: (() => void) | null = null;

export function setUnauthorisedHandler(fn: () => void): void {
  onUnauthorised = fn;
}

async function refreshTokens(): Promise<boolean> {
  const current = loadTokens();
  if (!current?.refreshToken) return false;

  const res = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  });
  if (!res.ok) {
    saveTokens(null);
    return false;
  }
  const data = (await res.json()) as Tokens;
  saveTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  return true;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Set for endpoints that return a file rather than JSON. */
  raw?: boolean;
}

export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(options.query ?? {})) {
    if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, String(v));
  }

  const send = async (): Promise<Response> => {
    const tokens = loadTokens();
    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (tokens?.accessToken) headers.authorization = `Bearer ${tokens.accessToken}`;

    return fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  };

  let res = await send();

  if (res.status === 401 && !path.startsWith('/auth/')) {
    refreshInFlight ??= refreshTokens().finally(() => {
      refreshInFlight = null;
    });
    const ok = await refreshInFlight;
    if (ok) {
      res = await send();
    } else {
      onUnauthorised?.();
      throw new ApiError(401, 'session expired, please sign in again');
    }
  }

  if (!res.ok) {
    let body: unknown;
    let message = `request failed with ${res.status}`;
    try {
      body = await res.json();
      const asRecord = body as { error?: string };
      if (asRecord?.error) message = asRecord.error;
    } catch {
      // Non-JSON error body; keep the generic message.
    }
    throw new ApiError(res.status, message, body);
  }

  if (options.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Builds the WebSocket URL for the live feed, carrying the access token. */
export function liveUrl(siteIds: string[] = []): string | null {
  const tokens = loadTokens();
  if (!tokens?.accessToken) return null;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = new URL(`${proto}//${window.location.host}${BASE}/live`);
  url.searchParams.set('token', tokens.accessToken);
  if (siteIds.length) url.searchParams.set('sites', siteIds.join(','));
  return url.toString();
}
